import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button, H2 } from "@/shared/ui";
import { ROUTES, PLANS_CONFIG, getPlanConfigSafe } from "@/shared/config";
import { analytics } from "@/utils/analytics";
import { getUserSubscription, supabase } from "@/shared/db";
import type { PlanType } from "@/shared/config";
import { toast } from "sonner";
import {
  CreditCard,
  Check,
  ArrowLeft,
  Shield,
  Zap,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import type { StripeElementsOptions } from "@stripe/stripe-js";
import { CheckoutForm } from "../components/CheckoutForm";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || "");

const VALID_PLAN_TYPES: PlanType[] = Object.keys(
  PLANS_CONFIG
) as PlanType[];

export const CheckoutPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [planType, setPlanType] = useState<PlanType | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activating, setActivating] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState<{
    discountApplied: boolean;
    discountPercent: number | null;
    originalAmount: number;
    finalAmount: number;
  } | null>(null);

  useEffect(() => {
    if (!user) {
      navigate(ROUTES.AUTH);
      return;
    }

    const planParam = searchParams.get("plan");
    if (planParam && VALID_PLAN_TYPES.includes(planParam as PlanType)) {
      setPlanType(planParam as PlanType);
    } else {
      setPlanType("lawyer_free" as PlanType);
    }

    loadCurrentSubscription();
  }, [user, searchParams, navigate]);

  const loadCurrentSubscription = async () => {
    if (!user) return;

    try {
      const subscription = await getUserSubscription(user.id);
      setCurrentSubscription(subscription);
    } catch (error) {
      console.error("Error loading subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!user || !planType) return;
    if (planType === "lawyer_free") return;

    setProcessing(true);

    try {
      const { data: responseData, error } = await supabase.functions.invoke(
        "create-subscription",
        {
          method: "POST",
          body: {
            userId: user.id,
            planType,
            billingPeriod: "month",
            promoCode: promoCode.trim() || undefined,
          },
        }
      );

      if (error) {
        const msg =
          (responseData && typeof responseData === "object" && (responseData as { error?: string })?.error) ||
          (responseData && typeof responseData === "object" && (responseData as { details?: string })?.details) ||
          error.message;
        const isGenericHttpError =
          (error.message?.includes("2xx") || error.message?.includes("status code") || error.message?.includes("5xx") || error.message?.includes("4xx")) &&
          promoCode.trim().length > 0;
        throw new Error(
          isGenericHttpError ? t("payments.checkout.promoCodeInvalid") : msg
        );
      }

      const parsed = (responseData || {}) as {
        clientSecret?: string | null;
        subscriptionId?: string;
        error?: string;
        discountApplied?: boolean;
        discountPercent?: number | null;
        originalAmount?: number;
        finalAmount?: number;
      };

      if (parsed?.error) {
        throw new Error(
          parsed.error.toLowerCase().includes("invalid") && parsed.error.toLowerCase().includes("promo")
            ? t("payments.checkout.promoCodeInvalid")
            : parsed.error
        );
      }

      if (!parsed?.clientSecret) {
        throw new Error("No client secret returned");
      }

      setClientSecret(parsed.clientSecret);
      setSubscriptionId(parsed.subscriptionId ?? null);
      if (parsed.discountApplied) {
        const fallbackPrice = getPlanConfigSafe(planType)?.price ?? 0;
        setDiscount({
          discountApplied: true,
          discountPercent: parsed.discountPercent ?? null,
          originalAmount: parsed.originalAmount ?? fallbackPrice,
          finalAmount: parsed.finalAmount ?? fallbackPrice,
        });
      } else {
        setDiscount(null);
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      toast.error(
        error instanceof Error ? error.message : t("payments.checkout.paymentError")
      );
    } finally {
      setProcessing(false);
    }
  };

  const syncSubscriptionStatus = async (): Promise<boolean> => {
    if (!user || !planType || !subscriptionId) {
      return false;
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        "sync-subscription-status",
        {
          method: "POST",
          body: {
            userId: user.id,
            subscriptionId,
            expectedPlanType: planType,
          },
        }
      );

      if (error) {
        console.warn("[checkout-page] sync-subscription-status:", error.message);
        return false;
      }

      const parsed = (data && typeof data === "object" ? data : {}) as {
        status?: string;
        planType?: string;
      };
      return parsed.status === "active" && parsed.planType === planType;
    } catch (err) {
      console.warn("[checkout-page] sync-subscription-status failed:", err);
      return false;
    }
  };

  const handlePaymentSuccess = async () => {
    if (planType) {
      const planConfig = getPlanConfigSafe(planType);
      const price = planConfig?.price ?? 0;
      analytics.subscriptionStarted(planType, price);
    }
    toast.success(t("payments.checkout.paymentProcessed"));
    setActivating(true);

    const maxAttempts = 10;
    const pollInterval = 2000;
    let isActivated = false;

    for (let i = 0; i < maxAttempts; i++) {
      await syncSubscriptionStatus();
      const sub = await getUserSubscription(user!.id);
      if (sub?.plan_type === planType && sub?.status === "active") {
        isActivated = true;
        break;
      }
      if (i < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, pollInterval));
      }
    }

    setActivating(false);
    if (!isActivated) {
      toast.error(t("payments.checkout.activationDelayed"));
    }
    navigate(ROUTES.DASHBOARD);
  };

  const handlePaymentError = (error: string) => {
    toast.error(error);
  };

  if (loading || !planType) {
    return (
      <div className="min-h-screen bg-linear-to-br from-nieve via-romance-50 to-nieve flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-tiber-500 animate-spin" />
      </div>
    );
  }

  const planConfig = getPlanConfigSafe(planType);
  if (!planConfig) {
    return (
      <div className="min-h-screen bg-linear-to-br from-nieve via-romance-50 to-nieve flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-tiber-500 animate-spin" />
      </div>
    );
  }

  const planName =
    i18n.language === "es" ? planConfig.nameEs : planConfig.name;
  const planFeatures =
    i18n.language === "es" ? planConfig.featuresEs : planConfig.features;
  const isUpgrade =
    currentSubscription &&
    currentSubscription.plan_type !== planType &&
    currentSubscription.status === "active";
  const summaryOriginal = discount?.discountApplied
    ? discount.originalAmount
    : planConfig.price;
  const summaryFinal = discount?.discountApplied
    ? discount.finalAmount
    : planConfig.price;
  const summaryDiscountAmount = Math.max(summaryOriginal - summaryFinal, 0);

  return (
    <div className="min-h-screen bg-linear-to-br from-nieve via-romance-50 to-nieve py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTES.PRICING)}
            leftIcon={<ArrowLeft className="w-5 h-5" />}
            className="mb-6"
          >
            {t("common.back")}
          </Button>

          <H2 className="text-3xl font-bold text-tiber-900 mb-2">
            {t("payments.checkout.title")}
          </H2>
          <p className="text-tiber-600">{t("payments.checkout.subtitle")}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Plan Summary */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-lg border border-tiber-200 p-6 mb-6"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-linear-to-br from-tiber-500 to-tiber-600 rounded-xl">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-tiber-900">
                    {planName}
                  </h3>
                  <p className="text-tiber-600">
                    {t("payments.checkout.selectedPlan")}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                {planFeatures.map((feature: string, index: number) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-atlantis-600 mt-0.5 shrink-0" />
                    <span className="text-tiber-700">{feature}</span>
                  </div>
                ))}
              </div>

              {isUpgrade && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        {t("payments.checkout.planUpgrade")}
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        {t("payments.checkout.upgradeMessage")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Payment Method */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg border border-tiber-200 p-6"
            >
              <h3 className="text-lg font-bold text-tiber-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {t("payments.checkout.paymentMethod")}
              </h3>

              <div className="bg-romance-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-tiber-600 mb-2">
                  {t("payments.checkout.securePayment")}
                </p>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-atlantis-600" />
                  <span className="text-xs text-tiber-600">
                    {t("payments.checkout.sslEncryption")}
                  </span>
                </div>
              </div>

              <p className="text-xs text-tiber-500">
                {t("payments.checkout.redirectMessage")}
              </p>
            </motion.div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-lg border border-tiber-200 p-6 sticky top-6"
            >
              <h3 className="text-lg font-bold text-tiber-900 mb-4">
                {t("payments.checkout.orderSummary")}
              </h3>

              <div className="mb-4">
                <label
                  htmlFor="promo-code-page"
                  className="block text-sm font-medium text-tiber-700 mb-1"
                >
                  {t("payments.checkout.promoCodeLabel")}
                </label>
                <input
                  id="promo-code-page"
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  disabled={processing || activating || !!clientSecret}
                  placeholder={t("payments.checkout.promoCodePlaceholder")}
                  className="w-full rounded-lg border border-tiber-300 px-3 py-2 text-sm text-tiber-900 focus:outline-none focus:ring-2 focus:ring-tiber-400 disabled:bg-tiber-50 disabled:text-tiber-500"
                />
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-tiber-600">{planName}</span>
                  <span className="font-semibold text-tiber-900">
                    ${summaryOriginal.toFixed(2)}
                  </span>
                </div>

                <div className="border-t border-tiber-200 pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-tiber-600">
                      {t("payments.checkout.subtotal")}
                    </span>
                    <span className="text-sm font-medium text-tiber-900">
                      ${summaryOriginal.toFixed(2)}
                    </span>
                  </div>
                  {discount?.discountApplied && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-atlantis-700">
                        {t("payments.checkout.discountApplied")}
                        {discount.discountPercent
                          ? ` (${discount.discountPercent}% OFF)`
                          : ""}
                      </span>
                      <span className="text-sm font-medium text-atlantis-700">
                        -${summaryDiscountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-tiber-600">
                      {t("payments.checkout.taxes")}
                    </span>
                    <span className="text-sm font-medium text-tiber-900">
                      {t("payments.checkout.taxesCalculated")}
                    </span>
                  </div>
                </div>

                <div className="border-t border-tiber-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-tiber-900">
                      {discount?.discountApplied
                        ? t("payments.checkout.youPay")
                        : t("payments.checkout.total")}
                    </span>
                    <span className="text-lg font-bold text-tiber-500">
                      ${summaryFinal.toFixed(2)}
                      <span className="text-sm font-normal text-tiber-600">
                        /
                        {planConfig.period === "month"
                          ? t("payments.checkout.month")
                          : t("payments.checkout.year")}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {!clientSecret ? (
                <Button
                  variant="primary"
                  onClick={handleCheckout}
                  disabled={processing || activating}
                  isLoading={processing}
                  className="w-full"
                  size="lg"
                >
                  {processing
                    ? t("payments.checkout.processing")
                    : t("payments.checkout.continuePayment")}
                </Button>
              ) : (
                <div className="space-y-4">
                  {activating && (
                    <div className="text-center py-4">
                      <Loader2 className="w-7 h-7 text-atlantis-500 animate-spin mx-auto mb-2" />
                      <p className="text-sm font-medium text-tiber-700">
                        {t("payments.checkout.activatingSubscription")}
                      </p>
                    </div>
                  )}
                  <Elements
                    stripe={stripePromise}
                    options={
                      {
                        clientSecret,
                        appearance: {
                          theme: "stripe",
                          variables: {
                            colorPrimary: "#0d9488",
                            colorBackground: "#ffffff",
                            colorText: "#1f2937",
                            fontFamily: "system-ui, sans-serif",
                            borderRadius: "8px",
                          },
                        },
                      } as StripeElementsOptions
                    }
                  >
                    <CheckoutForm
                      clientSecret={clientSecret}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  </Elements>
                </div>
              )}

              <p className="text-xs text-center text-tiber-500 mt-4">
                {t("payments.checkout.termsAgreement")}
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
